import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, Routes, Route, Navigate } from 'react-router-dom';
import {
  fetchFields,
  fetchSyncConfigs,
  fetchSyncStatus,
  fetchSyncObjects,
  fetchFullSyncStatus,
  triggerSyncNow,
  resetInitialLoad,
} from '../store/slices/analyticsSlice';
import { generateTraceId, logUserAction } from '../utils/trace';
import SyncConfigTab from '../components/SyncConfigTab';
import DataSourceSelector from '../components/DataSourceSelector';
import RelationshipsTab from '../components/analytics/RelationshipsTab';
import { fetchDataSources } from '../store/slices/dataSourcesSlice';
import BYODSourceDetail from '../components/analytics/BYODSourceDetail';
import CreateBYODSourceModal from '../components/analytics/CreateBYODSourceModal';

// Format a date as relative time (e.g., "2 hours ago", "Jan 15")
function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function SyncStatusContent() {
  const dispatch = useDispatch();
  const { syncConfig, tenantSync } = useSelector((state) => state.analytics);
  const { selectedDataSource } = useSelector((state) => state.dataSources);

  // Poll sync status
  useEffect(() => {
    dispatch(fetchFullSyncStatus());
    const interval = setInterval(() => {
      dispatch(fetchFullSyncStatus());
    }, 3000);
    return () => clearInterval(interval);
  }, [dispatch]);

  return (
    <div className="space-y-4">
      {/* Current Sync Status Card */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-bold text-gray-900">Sync Status</h2>
          <button
            onClick={() => dispatch(triggerSyncNow(selectedDataSource?.data_source_id))}
            disabled={syncConfig.activeWorkflow?.status === 'running' || syncConfig.activeWorkflow?.status === 'started' || tenantSync.isTriggeringSyncNow}
            className={`px-4 py-2 rounded-lg transition text-sm font-medium flex items-center gap-2 ${
              syncConfig.activeWorkflow?.status === 'running' || syncConfig.activeWorkflow?.status === 'started'
                ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                : 'bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50'
            }`}
          >
            {(syncConfig.activeWorkflow?.status === 'running' || syncConfig.activeWorkflow?.status === 'started' || tenantSync.isTriggeringSyncNow) && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {syncConfig.activeWorkflow?.status === 'running' || syncConfig.activeWorkflow?.status === 'started' ? 'Sync in Progress...' : 'Sync Now'}
          </button>
        </div>

        <div className="p-4">
          {/* Active Workflow Progress */}
          {(syncConfig.activeWorkflow?.status === 'running' || syncConfig.activeWorkflow?.status === 'started') ? (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="font-medium text-blue-800">
                  {syncConfig.activeWorkflow.current_step || 'Syncing...'}
                </span>
              </div>
              {syncConfig.activeWorkflow.objects_total > 0 && (
                <div className="mb-2">
                  <div className="flex justify-between text-sm text-blue-700 mb-1">
                    <span>Progress</span>
                    <span>{syncConfig.activeWorkflow.objects_processed || 0} / {syncConfig.activeWorkflow.objects_total} objects</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${syncConfig.activeWorkflow.objects_total > 0 ? (syncConfig.activeWorkflow.objects_processed / syncConfig.activeWorkflow.objects_total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}
              {syncConfig.activeWorkflow.current_object && (
                <p className="text-sm text-blue-600">Processing: {syncConfig.activeWorkflow.current_object}</p>
              )}
              {syncConfig.activeWorkflow.records_processed > 0 && (
                <p className="text-sm text-blue-600 mt-1">{syncConfig.activeWorkflow.records_processed.toLocaleString()} records synced</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Last Sync</div>
                <div className="font-medium text-gray-900">
                  {tenantSync.lastTenantSyncAt ? new Date(tenantSync.lastTenantSyncAt).toLocaleString() : 'Never'}
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Status</div>
                <div className="font-medium">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    tenantSync.lastTenantSyncStatus === 'completed' ? 'bg-green-100 text-green-700' :
                    tenantSync.lastTenantSyncStatus === 'failed' ? 'bg-red-100 text-red-700' :
                    tenantSync.lastTenantSyncStatus === 'running' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {tenantSync.lastTenantSyncStatus || 'Not synced'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="text-sm text-gray-500 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Auto-sync frequency: <span className="font-medium capitalize">{tenantSync.frequency}</span>
          </div>
        </div>
      </div>

      {/* Per-Object Sync Status */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-bold text-gray-900">Objects</h2>
        </div>
        {syncConfig.objects.length === 0 ? (
          <div className="p-8 text-center text-gray-600">
            <p className="mb-2">No objects configured for sync.</p>
            <p className="text-sm">Go to Sync Configuration to select objects to sync.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {syncConfig.objects.filter(obj => obj.is_synced).map((obj) => (
              <div key={obj.name} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                <div>
                  <span className="font-medium text-gray-900">{obj.label}</span>
                  <span className="text-xs text-gray-500 font-mono ml-2">{obj.name}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">
                    {obj.synced_field_count || 0} fields
                  </span>
                  {obj.record_count != null && (
                    <span className="text-gray-500">
                      {obj.record_count.toLocaleString()} records
                    </span>
                  )}
                  {obj.last_sync_at ? (
                    <span className="text-gray-400 text-xs" title={new Date(obj.last_sync_at).toLocaleString()}>
                      {formatRelativeTime(obj.last_sync_at)}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">Never synced</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function DataSourcesPage() {
  const dispatch = useDispatch();
  const { activeTenant, isLoading: isAuthLoading } = useSelector((state) => state.auth);
  const {
    initialLoadComplete,
    initialLoadStatus,
  } = useSelector((state) => state.analytics);
  const { selectedDataSource } = useSelector((state) => state.dataSources);

  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const [showCreateBYODModal, setShowCreateBYODModal] = useState(false);

  // Track loading start time
  useEffect(() => {
    if (!initialLoadComplete && !loadingStartTime) {
      setLoadingStartTime(Date.now());
    }
    if (initialLoadComplete) {
      setLoadingStartTime(null);
      setLoadingTimedOut(false);
    }
  }, [initialLoadComplete, loadingStartTime]);

  // Timeout after 15 seconds of loading
  useEffect(() => {
    if (!loadingStartTime || initialLoadComplete) return;

    const timer = setTimeout(() => {
      if (!initialLoadComplete) {
        setLoadingTimedOut(true);
      }
    }, 15000);

    return () => clearTimeout(timer);
  }, [loadingStartTime, initialLoadComplete]);

  // Retry loading function
  const handleRetryLoad = useCallback(() => {
    setLoadingTimedOut(false);
    setLoadingStartTime(Date.now());
    dispatch(resetInitialLoad());
    const traceId = generateTraceId();
    dispatch(fetchFields({ traceId }));
    dispatch(fetchSyncConfigs({ traceId }));
    dispatch(fetchSyncStatus({ traceId }));
    dispatch(fetchFullSyncStatus());
  }, [dispatch]);

  useEffect(() => {
    if (activeTenant) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'page_load', { page: 'data-sources' });
      dispatch(fetchFields({ traceId }));
      dispatch(fetchSyncConfigs({ traceId }));
      dispatch(fetchSyncStatus({ traceId }));
      dispatch(fetchFullSyncStatus());
      dispatch(fetchDataSources({ traceId }));
    }
  }, [dispatch, activeTenant]);

  // Re-fetch sync objects when selected data source changes
  useEffect(() => {
    if (activeTenant) {
      dispatch(fetchSyncObjects({ dataSourceId: selectedDataSource?.data_source_id }));
    }
  }, [dispatch, activeTenant, selectedDataSource?.data_source_id]);

  const tabLinkClasses = ({ isActive }) =>
    `pb-4 px-2 font-medium transition ${
      isActive
        ? 'text-primary-600 border-b-2 border-primary-600'
        : 'text-gray-500 hover:text-gray-700'
    }`;

  // Compute loading steps/errors for loading state UI
  const { loadingSteps, errors } = useMemo(() => {
    const steps = [];
    const errs = [];

    if (isAuthLoading || !activeTenant) {
      steps.push('Authenticating');
    } else {
      if (initialLoadStatus.syncStatus === 'loading') {
        steps.push('Checking data source connection');
      } else if (initialLoadStatus.syncStatus === 'error') {
        errs.push({ step: 'Data source connection', error: initialLoadStatus.syncStatusError });
      }

      if (initialLoadStatus.fields === 'loading') {
        steps.push('Loading field metadata');
      } else if (initialLoadStatus.fields === 'error') {
        errs.push({ step: 'Field metadata', error: initialLoadStatus.fieldsError });
      }

      if (initialLoadStatus.fullSyncStatus === 'loading') {
        steps.push('Loading sync configuration');
      } else if (initialLoadStatus.fullSyncStatus === 'error') {
        errs.push({ step: 'Sync configuration', error: initialLoadStatus.fullSyncStatusError });
      }

      if (steps.length === 0 && errs.length === 0) {
        steps.push('Starting...');
      }
    }

    return { loadingSteps: steps, errors: errs };
  }, [isAuthLoading, activeTenant, initialLoadStatus]);

  if (!initialLoadComplete) {
    if (loadingTimedOut) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="bg-amber-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Taking longer than expected</h2>
            <p className="text-gray-600 mb-4">
              The analytics service is slow to respond. This could be due to network issues or the service being busy.
            </p>

            {loadingSteps.length > 0 && (
              <div className="mb-4 text-sm text-gray-500">
                <p className="mb-2">Still waiting for:</p>
                <ul className="space-y-1">
                  {loadingSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ul>
              </div>
            )}

            {errors.length > 0 && (
              <div className="mb-4 text-left bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-medium text-red-800 mb-2">Errors encountered:</h3>
                <ul className="space-y-2 text-sm text-red-700">
                  {errors.map((err, i) => (
                    <li key={i}>
                      <span className="font-medium">{err.step}:</span> {err.error || 'Request failed'}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={handleRetryLoad}
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">Loading data sources...</p>

          {loadingSteps.length > 0 && (
            <div className="mt-4 text-sm text-gray-500">
              <ul className="space-y-1">
                {loadingSteps.map((step, i) => (
                  <li key={i} className="flex items-center justify-center gap-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                    {step}...
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errors.length > 0 && (
            <div className="mt-4 text-left bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-800 mb-2">Loading errors:</h3>
              <ul className="space-y-2 text-sm text-red-700">
                {errors.map((err, i) => (
                  <li key={i}>
                    <span className="font-medium">{err.step}:</span> {err.error || 'Unknown error'}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleRetryLoad}
                className="mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Retry loading
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Data Source Selector - Always visible */}
      <DataSourceSelector showAlways={true} />

      <div className="px-6 py-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Data Sources</h1>
          <button
            onClick={() => setShowCreateBYODModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            data-testid="add-file-source-btn"
          >
            Add File Source
          </button>
        </div>
        {showCreateBYODModal && (
          <CreateBYODSourceModal onClose={() => setShowCreateBYODModal(false)} />
        )}

        {/* Sub-tab navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4">
            <NavLink
              to="/data-sources/sync-config"
              className={tabLinkClasses}
              data-testid="ds-tab-sync-config"
            >
              Sync Configuration
            </NavLink>
            <NavLink
              to="/data-sources/sync-status"
              className={tabLinkClasses}
              data-testid="ds-tab-sync-status"
            >
              Sync Status
            </NavLink>
            <NavLink
              to="/data-sources/relationships"
              className={tabLinkClasses}
              data-testid="ds-tab-relationships"
            >
              Relationships
            </NavLink>
            {selectedDataSource?.source_type === 'file' && (
              <NavLink
                to="/data-sources/tables"
                className={tabLinkClasses}
                data-testid="ds-tab-tables"
              >
                Tables
              </NavLink>
            )}
          </nav>
        </div>

        {/* Sub-tab content via nested routes */}
        <Routes>
          <Route index element={<Navigate to="sync-config" replace />} />
          <Route path="sync-config" element={<SyncConfigTab />} />
          <Route path="sync-status" element={<SyncStatusContent />} />
          <Route path="relationships" element={<RelationshipsTab />} />
          <Route path="tables" element={<BYODSourceDetail dataSource={selectedDataSource} />} />
          <Route path="*" element={<Navigate to="sync-config" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default DataSourcesPage;
